import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import NumberFormat from "react-number-format";

import UploadIcon from "@mui/icons-material/Upload";
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid2 as Grid,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useDateFormatSelect, useDealDefaultCurrencySelect } from "store";
import toIsoMidday from "utils/to-iso-midday";

import { currencySign } from "../../constants";
import { ReferenceIndexSelect } from "./form-fields/ReferenceIndexSelect";
import { FormValues } from "./schemas";

interface Props {
  formik: FormikProps<FormValues>;
  updateIndexValue: (index: string, date: string | null, type: "principal" | "interest" | "pik") => Promise<void>;
  size?: "small" | "large";
  disabledFields?: string[];
  hiddenFields?: string[];
}

export const AmoritzationDrawerPrincipalSection: React.FC<Props> = ({
  formik,
  updateIndexValue,
  size = "small",
  disabledFields = [],
  hiddenFields = []
}) => {
  const { t } = useTranslation("debt");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>("");
  const currency = useDealDefaultCurrencySelect() || "USD";
  const dateFormat = useDateFormatSelect();
  const gridSize = size === "large" ? 3 : 4;

  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      formik.setFieldValue("custom_payments_file", file);
      setFileName(file.name);
    } else {
      formik.setFieldValue("custom_payments_file", null);
      setFileName("");
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("principalSection.title")}
        </Typography>
        <Divider />
      </Box>
      <Grid container spacing={5}>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("principal_reference_index") ? "none" : "block" }}>
          <ReferenceIndexSelect
            name="principal_reference_index"
            formik={formik}
            label={t("principalSection.index")}
            disabled={disabledFields.includes("principal_reference_index")}
            size={size === "large" ? "medium" : "small"}
            onIndexChange={(index) =>
              updateIndexValue(index, formik.values.principal_reference_day || null, "principal")
            }
            onIndexClear={() => formik.setFieldValue("principal_link_percent", 0)}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("principal_index_value") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled
            name="principal_index_value"
            label={t("principalSection.indexAtReferenceValue")}
            value={formik.values.principal_index_value}
            onChange={formik.handleChange}
            error={formik.touched.principal_index_value && Boolean(formik.errors.principal_index_value)}
            helperText={formik.touched.principal_index_value && formik.errors.principal_index_value}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("principal_link_percent") ? "none" : "block" }}>
          <TextField
            fullWidth
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
            type="number"
            disabled={!formik.values.principal_reference_index || disabledFields.includes("principal_link_percent")}
            name="principal_link_percent"
            label={t("principalSection.percentage")}
            value={formik.values.principal_link_percent}
            onChange={formik.handleChange}
            error={formik.touched.principal_link_percent && Boolean(formik.errors.principal_link_percent)}
            helperText={formik.touched.principal_link_percent && formik.errors.principal_link_percent}
          />
        </Grid>
        <Grid
          size={gridSize}
          sx={{ display: hiddenFields.includes("principal_first_payment_date") ? "none" : "block" }}
        >
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("principalSection.firstPaymentDate")}
            disabled={disabledFields.includes("principal_first_payment_date")}
            value={
              formik.values.principal_first_payment_date ? dayjs(formik.values.principal_first_payment_date) : null
            }
            onChange={(newValue) =>
              formik.setFieldValue("principal_first_payment_date", newValue ? toIsoMidday(newValue) : "")
            }
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error:
                  formik.touched.principal_first_payment_date && Boolean(formik.errors.principal_first_payment_date),
                helperText: formik.touched.principal_first_payment_date && formik.errors.principal_first_payment_date
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("principal_reference_day") ? "none" : "block" }}>
          <DesktopDatePicker
            format={dateFormat}
            label={t("principalSection.referenceDate")}
            disabled={disabledFields.includes("principal_reference_day")}
            value={formik.values.principal_reference_day ? dayjs(formik.values.principal_reference_day) : null}
            onChange={(newValue) => {
              formik.setFieldValue("principal_reference_day", newValue ? toIsoMidday(newValue) : "");
              if (formik.values.principal_reference_index) {
                updateIndexValue(
                  formik.values.principal_reference_index,
                  newValue ? toIsoMidday(newValue) : null,
                  "principal"
                );
              }
            }}
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: formik.touched.principal_reference_day && Boolean(formik.errors.principal_reference_day),
                helperText: formik.touched.principal_reference_day && formik.errors.principal_reference_day
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("principal_frequency") ? "none" : "block" }}>
          <Select
            fullWidth
            disabled={disabledFields.includes("principal_frequency")}
            name="principal_frequency"
            label={t("principalSection.frequency")}
            value={formik.values.principal_frequency}
            variant="outlined"
            items={[
              { key: "month", value: "month", text: t("common.monthly") },
              { key: "quarter", value: "quarter", text: t("common.quarterly") },
              { key: "semi-annual", value: "semi-annual", text: t("common.semiAnnual") },
              { key: "year", value: "year", text: t("common.annually") }
            ]}
            error={formik.touched.principal_frequency && Boolean(formik.errors.principal_frequency)}
            helperText={formik.touched.principal_frequency ? formik.errors.principal_frequency : undefined}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("balloon_payment") ? "none" : "block" }}>
          <NumberFormat
            name="balloon_payment"
            label={t("principalSection.balloonPayment")}
            thousandSeparator
            isNumericString
            placeholder="$0.00"
            variant="outlined"
            prefix={currencySign[currency]}
            error={formik.touched.balloon_payment && Boolean(formik.errors.balloon_payment)}
            helperText={formik.touched.balloon_payment && formik.errors.balloon_payment}
            decimalScale={2}
            allowNegative={false}
            customInput={TextField}
            value={formik.values.balloon_payment}
            onValueChange={({ value }) => formik.setFieldValue("balloon_payment", Number(value))}
            disabled={disabledFields.includes("balloon_payment")}
          />
        </Grid>
        {formik.values.payment_term === "custom" && (
          <Grid size={gridSize} sx={{ display: hiddenFields.includes("custom_payments_file") ? "none" : "block" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
              name="custom_payments_file"
            />
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleFileButtonClick}
              fullWidth
              sx={{ height: "40px", fontSize: "14px" }}
            >
              {fileName ? fileName.split("\\").pop() : t("principalSection.payments")}
            </Button>
            {formik.touched.custom_payments_file && formik.errors.custom_payments_file && (
              <FormHelperText error>{formik.errors.custom_payments_file as string}</FormHelperText>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
